import * as React from "react";
import { useQuery } from "react-query";
import { DataTable } from "components/DataTable/DataTable";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TablePagination, Stack } from "@mui/material";
import { ClientMtUsageReportItem, ReportFileInfo, MetricsService } from "gen/clients";
import { describeMtBillingType } from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { secondsToMinutes } from "utils/dateUtils";
import { MtUsageReportFilters } from "./MtUsageReportFilter";

interface Props {
  reportId: string;
  jsonFile: ReportFileInfo;
  filters?: MtUsageReportFilters;
}

const MtUsageReportTable: React.FC<Props> = ({ reportId, jsonFile, filters }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const {
    data: reportData,
    isLoading,
    error
  } = useQuery<ClientMtUsageReportItem[]>(["mtUsageReportData", reportId, jsonFile.s3Key], {
    queryFn: async () => {
      if (!jsonFile.s3Key) {
        throw new Error("JSON file s3Key is missing");
      }

      const downloadResponse = await MetricsService.downloadReportJobFile({
        reportId,
        s3Key: encodeURIComponent(jsonFile.s3Key)
      });

      const response = await fetch(downloadResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch report data: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!jsonFile.s3Key,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });

  const filteredData = React.useMemo(() => {
    if (!reportData || !filters) {
      return reportData || [];
    }

    return reportData.filter((row: ClientMtUsageReportItem) => {
      if (filters.clientName && !row.clientName?.toLowerCase().includes(filters.clientName.toLowerCase())) {
        return false;
      }
      if (filters.clientId && !row.clientId?.toLowerCase().includes(filters.clientId.toLowerCase())) {
        return false;
      }
      if (filters.branch && !(row.branchName || "").toLowerCase().includes(filters.branch.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [reportData, filters]);

  const pageData = React.useMemo(() => {
    if (!filteredData) {
      return [];
    }
    return filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(0);
  }, [filters]);

  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "clientName",
            title: "Client Name",
            cell: (row: ClientMtUsageReportItem) => row.clientName
          },
          {
            id: "clientId",
            title: "Client ID",
            cell: (row: ClientMtUsageReportItem) => row.clientId
          },
          {
            id: "branchName",
            title: "Branch",
            cell: (row: ClientMtUsageReportItem) => row.branchName || ""
          },
          {
            id: "billingType",
            title: "Billing Type",
            cell: (row: ClientMtUsageReportItem) => describeMtBillingType(row.billingType)
          },
          {
            id: "userCount",
            title: "User Count",
            cell: (row: ClientMtUsageReportItem) => row.usernames?.length || 0
          },
          {
            id: "documentCharactersCharged",
            title: "Document Translation (characters)",
            cell: (row: ClientMtUsageReportItem) => row.charactersChargedByUserRequest?.FILES_TRANSLATION || 0
          },
          {
            id: "mediaTranscriptionDuration",
            title: "Transcription (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0)
          },
          {
            id: "mediaTranscriptionAndTranslationDuration",
            title: "Transcription And Translation (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0)
          },
          {
            id: "mediaTransclationAndDubbingDuration",
            title: "Translation And Dubbing (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_DUBBING || 0)
          },
          {
            id: "mediaTranslationAndSubtitlingDuration",
            title: "Translation And Subtitling (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0)
          }
        ]}
        data={pageData}
        rowKey={(row: ClientMtUsageReportItem) => row.clientId}
        dense={true}
      />
      <TablePagination
        count={filteredData?.length || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={e => setRowsPerPage(+e.target.value)}
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Stack>
  );
};

export { MtUsageReportTable };
